import { Icon } from '@chakra-ui/icons'
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import numeral from 'numeral'
import type { ReactElement } from 'react'
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FiChevronDown } from 'react-icons/fi'
import { useHistory } from 'react-router'
import type { CellProps, Column } from 'react-table'
import { useRowSelect, useRowState, useTable } from 'react-table'
import { isEqual, isUndefined } from 'lodash'
import {
  filesPerPage,
  getExtensionFromFileTypeAndMimeType,
  getExtensionFromMimeType,
  mediaLibraryThumbnailTransformation,
} from '../../../../../utils/mediaLibrary'
import type {
  CopyMoveOperations,
  MediaLibrarySearchFileResponse,
} from '../../../../interfaces'
import { MLContext, UserContext } from '../../../../providers'
import {
  MediaLibraryActions,
  defaultUrlState,
} from '../../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../../providers/PublicLinksInfoProvider'
import { ContextMenuTrigger } from '../../ContextMenu'
import FilePreview from '../../FilePreview'
import { CopyMoveDrawer, DeleteMediaModal } from '../../MediaOperations'
import { FeaturesEnum } from '../../../../../utils/features'
import useToast from '../../../../hooks/useToast'
import FileListLoader from './FileListLoader'

enum TableActions {
  SELECT_FILE = 'SELECT_FILE',
}

interface Props {
  loadComplete: { files: boolean; folders: boolean }
  setOnLoadComplete(type: 'file'): void
}

const FilesList = ({
  loadComplete,
  setOnLoadComplete,
}: Props): ReactElement | null => {
  const { isPublicLink, urlPrefix } = useContext(PublicLinksInfoContext)
  const toast = useToast()
  const { user } = useContext(UserContext)
  const [
    {
      fileList,
      filePage,
      selectedFiles,
      loadingFiles,
      selectedFolders,
      mediaCollectionRoute,
      currentMediaCollection,
      abortFilesRequest,
      urlState,
      folderPath,
      queryParams,
    },
    dispatch,
  ] = useContext(MLContext)

  const [gridData, setGridData] = useState({
    filePage,
    loadingFiles: false,
    fileList,
  })

  const [copyMoveOperation, setCopyMoveOperation] =
    useState<CopyMoveOperations>('copy')

  const {
    isOpen: isOpenDeleteMediaModal,
    onOpen: onOpenDeleteMediaModal,
    onClose: onCloseDeleteMediaModal,
  } = useDisclosure()

  const {
    isOpen: isOpenCopyMoveDrawer,
    onOpen: onOpenCopyMoveDrawer,
    onClose: onCloseCopyMoveDrawer,
  } = useDisclosure()

  const history = useHistory()

  const { t } = useTranslation()

  const columns = useMemo<Array<Column<MediaLibrarySearchFileResponse>>>(
    () => [
      {
        id: 'selected',
        width: '4.1em',
        Cell: ({ row }: CellProps<MediaLibrarySearchFileResponse>) => (
          <Checkbox isChecked={row.isSelected} pointerEvents="none" />
        ),
      },
      {
        Header: t('name', 'Name').toString(),
        accessor: values => ({
          name: values.name,
          type: values.type,
          extension: getExtensionFromMimeType(values.mime),
        }),
        // eslint-disable-next-line @typescript-eslint/member-delimiter-style
        Cell: ({
          value,
          row,
        }: {
          value: { name: string; extension: string; type: string }
          row: any
        }) => (
          <Flex align="center">
            <Box w="40px" mr="0.5rem">
              <AspectRatio w="40px" h="40px">
                <FilePreview
                  file={row.original}
                  folderPath={row.original.filePath}
                  transformation={mediaLibraryThumbnailTransformation}
                  disablePadding={true}
                />
              </AspectRatio>
            </Box>
            <Tooltip label={value.name}>
              <Text className="asset-name" isTruncated>
                {value.name}
              </Text>
            </Tooltip>
            <Spacer />
            <Flex gap={2}>
              {!isPublicLink && row.original.isPublished === false ? (
                <Badge ml="auto" colorScheme="pink">
                  {t('draft_uppercase', 'DRAFT')}
                </Badge>
              ) : null}
              {value.type === 'file-version' ? (
                <Badge ml="auto" colorScheme="purple">
                  {t('file_version', 'FILE VERSION')}
                </Badge>
              ) : null}
            </Flex>
          </Flex>
        ),
        width: '22em',
      },
      {
        Header: t('file_type', 'File Type').toString(),
        accessor: values =>
          `${(
            getExtensionFromFileTypeAndMimeType(values) ?? ''
          ).toUpperCase()} File`,
        width: '6em',
      },
      {
        Header: t('file_size', 'File Size').toString(),
        accessor: values => values.size,
        Cell: ({ value }: { value: number }) =>
          numeral(value).format('0.00 ib').replace('i', ''),
        width: '7em',
      },
      {
        Header: t('last_modified', 'Last Modified').toString(),
        accessor: values => moment(values.updatedAt),
        Cell: ({ value }: { value: moment.Moment }) =>
          value.format('MM/DD/YYYY'),
        width: '7.85em',
      },
    ],
    [],
  )

  const data = useMemo(() => gridData.fileList, [gridData.fileList])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    toggleRowSelected,
    toggleAllRowsSelected,
    dispatch: tableDispatch,
    setRowState,
  } = useTable<MediaLibrarySearchFileResponse>(
    {
      columns,
      data,
      getRowId: row => row.versionInfo.id,
      initialState: {
        selectedRowIds: selectedFiles.reduce(
          (acc, x) => ({ ...acc, [x.versionInfo.id]: true }),
          {},
        ),
        rowState: data.reduce(
          (acc, x) => ({
            ...acc,
            [x.versionInfo.id]: {
              copyActionDisabled:
                selectedFiles.length > 1 || selectedFolders.length > 0,
              moveActionDisabled:
                selectedFiles.length > 1 || selectedFolders.length > 0,
              deleteActionDisabled:
                selectedFiles.length > 1 || selectedFolders.length > 0,
            },
          }),
          {},
        ),
      },
      stateReducer: (newState, action) => {
        if (action.type === TableActions.SELECT_FILE) {
          if (!action.value) {
            dispatch([
              {
                type: MediaLibraryActions.SET_SELECTED_FILES,
                payload: selectedFiles.filter(
                  x => x.versionInfo.id !== action.id,
                ),
              },
              {
                type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
              },
            ])
          } else {
            const newFile = fileList.find(x => x.versionInfo.id === action.id)
            if (newFile) {
              dispatch([
                {
                  type: MediaLibraryActions.SET_SELECTED_FILES,
                  payload: [...selectedFiles, newFile],
                },

                {
                  type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                },
              ])
            }
          }
        }

        return newState
      },
    },
    useRowSelect,
    useRowState,
  )

  useEffect(() => {
    toggleAllRowsSelected(false)
    selectedFiles.map(x => void toggleRowSelected(x.versionInfo.id, true))
  }, [selectedFiles])

  useEffect(() => {
    fileList.map(
      x =>
        void setRowState([x.versionInfo.id], prevValue => {
          prevValue.copyActionDisabled =
            selectedFiles.length > 1 || selectedFolders.length > 0
          prevValue.moveActionDisabled =
            selectedFiles.length > 1 || selectedFolders.length > 0
          prevValue.deleteActionDisabled =
            selectedFiles.length > 1 || selectedFolders.length > 0
          return prevValue
        }),
    )
  }, [selectedFiles, selectedFolders])

  // to save file data comming from MLProvider in gridData.fileList
  useEffect(() => {
    setGridData(prev => ({ ...prev, fileList, filePage }))
  }, [filePage, loadingFiles, fileList])

  const generateUrl = useCallback((): string => {
    const params: Record<string, string> = {
      skip: (gridData.filePage * filesPerPage).toString(),
      limit: filesPerPage.toString(),
      sort: urlState.sort,
      searchQuery: urlState.searchQuery
        ? `(${urlState.fileTypeQuery}) AND ${urlState.searchQuery}`
        : urlState.fileTypeQuery,
    }
    if (urlState.semanticUrl) {
      params.semanticUrl = urlState.semanticUrl
    } else if (urlState.semantic) {
      params.semantic = urlState.semantic
    } else {
      const path =
        urlState.searchQuery === '' &&
        urlState.fileTypeQuery === defaultUrlState.fileTypeQuery
          ? folderPath
          : null
      if (path !== null) {
        params.path = path
      }
    }

    if (!isUndefined(urlState.minScore)) {
      params.minScore = urlState.minScore.toString()
    }

    const searchParams = new URLSearchParams(params)

    return `?${searchParams.toString()}`
  }, [gridData.filePage, filesPerPage, urlState, folderPath])

  const loadData = useCallback(() => {
    const collectionId =
      queryParams?.searchQuery === ''
        ? urlState.currentMediaCollection
          ? urlState.currentMediaCollection
          : undefined
        : undefined
    let endpoint

    if (typeof collectionId === 'string') {
      endpoint = `${urlPrefix}api/v2/media-collections/${collectionId}/assets${generateUrl()}`
    } else {
      endpoint = `${urlPrefix}api/v2/files${generateUrl()}`
    }

    if (
      !isPublicLink &&
      endpoint?.startsWith(`${urlPrefix}api/v2/media-collections`) &&
      !user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      return null
    }

    fetch(endpoint)
      .then(response => response.json())
      .then(data => {
        if (!Array.isArray(data)) {
          throw data
        }
        setGridData(prev => ({
          ...prev,
          loadingFiles: false,
          fileList: [...prev.fileList, ...data],
        }))
        dispatch([
          {
            type: MediaLibraryActions.SET_FILE_LIST,
            payload: [...gridData.fileList, ...data],
          },
          {
            type: MediaLibraryActions.LOAD_MORE_FILES,
          },
        ])
      })
      .catch(error => {
        toast({
          description: 'Something went wrong!',
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        setGridData(prev => ({
          ...prev,
          loadingFiles: false,
          fileList: [],
        }))
        dispatch([
          {
            type: MediaLibraryActions.SET_FILE_LIST,
            payload: [],
          },
          {
            type: MediaLibraryActions.SET_FILE_ERROR,
            payload:
              (error as { message?: string }).message ?? 'Something went wrong',
          },
        ])
      })
  }, [
    generateUrl,
    gridData.fileList,
    dispatch,
    setGridData,
    queryParams,
    urlState,
    user,
    FeaturesEnum,
  ])

  useEffect(() => {
    if (gridData.filePage && gridData.filePage !== filePage) {
      loadData()
    }
  }, [gridData.filePage, filePage])

  useEffect(() => {
    if (isEqual(gridData.fileList, fileList)) {
      setOnLoadComplete('file')
    }
  }, [gridData.fileList, fileList])

  if (!(loadComplete.files && loadComplete.folders)) {
    return <FileListLoader />
  }

  if (gridData.fileList.length === 0 && !gridData.loadingFiles) {
    return null
  }

  return (
    <Fragment>
      <Text fontWeight="semibold" color="appSlate">
        {t('files', 'Files')}
      </Text>
      <Table {...getTableProps}>
        <Thead bgColor="appPaleGreyTwo">
          {headerGroups.map(headerGroup => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps()
            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map((column, i) => {
                  const { key: keyCol, ...restColumn } = column.getHeaderProps()
                  return (
                    <Th
                      key={keyCol}
                      {...restColumn}
                      width={i === 0 ? 10 : column.width}
                    >
                      {column.render('Header')}
                    </Th>
                  )
                })}
              </Tr>
            )
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row)
            const { key, ...restRowProps } = row.getRowProps()
            return (
              <ContextMenuTrigger
                key={key}
                onTrigger={event => {
                  if (
                    !selectedFiles
                      .map(x => x.versionInfo.id)
                      .includes(row.original.versionInfo.id)
                  ) {
                    dispatch([
                      {
                        type: MediaLibraryActions.SELECT_FILE,
                        payload: {
                          file: row.original,
                          event,
                        },
                      },
                      {
                        type: MediaLibraryActions.DESELECT_ALL_MEDIA_COLLECTIONS,
                      },
                    ])
                  }
                }}
                dispatch={dispatch}
              >
                {contextMenuProps => (
                  <Tr
                    {...restRowProps}
                    data-id={`file-${row.original.fileId}`}
                    className="mouse-select__selectable"
                  >
                    {row.cells.map(cell => {
                      const { key: keyRow, ...restCellProps } =
                        cell.getCellProps()
                      return (
                        <Td
                          key={keyRow}
                          {...restCellProps}
                          width={cell.column.width}
                          maxW="18em"
                          cursor="pointer"
                          onClick={e => {
                            e.stopPropagation()
                            toggleRowSelected(
                              row.original.versionInfo.id,
                              !row.isSelected,
                            )
                            tableDispatch({
                              type: TableActions.SELECT_FILE,
                              id: row.original.versionInfo.id,
                              value: !row.isSelected,
                            })
                          }}
                          onDoubleClick={() => {
                            dispatch([
                              {
                                type: MediaLibraryActions.DESELECT_ALL_FILES_AND_FOLDERS,
                              },
                            ])
                            abortFilesRequest()
                            mediaCollectionRoute
                              ? history.push(
                                  `/media-collections/${currentMediaCollection}/detail/${row.original.versionInfo.id}`,
                                )
                              : history.push(
                                  `/media-library/detail/${row.original.versionInfo.id}`,
                                )
                          }}
                          {...contextMenuProps}
                        >
                          <Text isTruncated>{cell.render('Cell')}</Text>
                        </Td>
                      )
                    })}
                  </Tr>
                )}
              </ContextMenuTrigger>
            )
          })}
        </Tbody>
      </Table>
      {(gridData.fileList.length % filesPerPage === 0 &&
        gridData.fileList.length / filesPerPage === gridData.filePage + 1) ||
      gridData.loadingFiles ? (
        <Button
          as="a"
          variant="link"
          cursor="pointer"
          color="appClearBlue"
          onClick={() => {
            setGridData(prev => ({
              ...prev,
              filePage: prev.filePage + 1,
              loadingFiles: true,
            }))
          }}
          isLoading={gridData.loadingFiles}
          loadingText={t('loading', 'Loading...')}
        >
          {t('see_more', 'See More')}
          <Icon as={FiChevronDown} />
        </Button>
      ) : null}
      {isOpenDeleteMediaModal && (
        <DeleteMediaModal
          isOpen={isOpenDeleteMediaModal}
          onClose={onCloseDeleteMediaModal}
        />
      )}
      {isOpenCopyMoveDrawer && (
        <CopyMoveDrawer
          operation={copyMoveOperation}
          isOpen={isOpenCopyMoveDrawer}
          onClose={onCloseCopyMoveDrawer}
        />
      )}
    </Fragment>
  )
}

export default FilesList
